import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { confirmResetPassword, resetPassword } from 'aws-amplify/auth';
import { useTranslation } from 'react-i18next';
import { PasswordInput } from '@/components/ui/PasswordInput';

function friendlyError(err: unknown): string {
  const name = err instanceof Error ? (err as { name?: string }).name ?? '' : '';
  if (name === 'UserNotFoundException' || name === 'InvalidParameterException') return 'err_not_found';
  if (name === 'CodeMismatchException' || name === 'ExpiredCodeException') return 'err_code_mismatch';
  if (name === 'InvalidPasswordException' || name === 'InvalidParameterException') return 'err_weak_password';
  return '';
}

export function ForgotPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [step, setStep] = useState<1 | 2>(1);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleRequestCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);
    try {
      await resetPassword({ username: email });
      setStep(2);
    } catch (err) {
      const key = friendlyError(err);
      setError(key ? t(`auth.${key}`) : (err instanceof Error ? err.message : t('auth.err_not_found')));
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError(t('auth.passwords_mismatch'));
      return;
    }
    setError(null);
    setIsLoading(true);
    try {
      await confirmResetPassword({ username: email, confirmationCode: code, newPassword });
      navigate('/login', { state: { passwordReset: true } });
    } catch (err) {
      const key = friendlyError(err);
      setError(key ? t(`auth.${key}`) : (err instanceof Error ? err.message : t('auth.err_code_mismatch')));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="page-center">
      <div className="phone-mockup">
        <div className="app-content" style={{ backgroundColor: 'white', justifyContent: 'center' }}>

          {/* Logo */}
          <div style={{ textAlign: 'center', marginBottom: '8px' }}>
            <h1 style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--primary)', margin: 0 }}>
              VADOStudy
            </h1>
          </div>

          {step === 1 ? (
            <>
              <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-dark)', margin: '0 0 8px' }}>
                {t('auth.forgot_title')}
              </h2>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', margin: '0 0 24px', lineHeight: 1.5 }}>
                {t('auth.forgot_desc')}
              </p>

              <form
                onSubmit={handleRequestCode}
                style={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%' }}
              >
                <div className="form-group">
                  <label htmlFor="fp-email">{t('auth.email_label')}</label>
                  <input
                    id="fp-email"
                    type="email"
                    placeholder={t('auth.email_placeholder')}
                    autoComplete="email"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setError(null); }}
                    disabled={isLoading}
                    required
                  />
                </div>

                {error && (
                  <p style={{
                    color: 'var(--danger)', fontSize: '0.85rem', margin: 0,
                    padding: '10px 14px', background: '#FEF2F2', borderRadius: '8px', lineHeight: 1.5,
                  }}>
                    ⚠️ {error}
                  </p>
                )}

                <button type="submit" className="btn btn-primary" disabled={isLoading}>
                  {isLoading ? '…' : t('auth.send_code')}
                </button>
              </form>
            </>
          ) : (
            <>
              <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-dark)', margin: '0 0 8px' }}>
                {t('auth.check_email_title')}
              </h2>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', margin: '0 0 24px', lineHeight: 1.5 }}>
                {t('auth.check_email_desc', { email })}
              </p>

              <form
                onSubmit={handleConfirmReset}
                style={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%' }}
              >
                <div className="form-group">
                  <label htmlFor="fp-code">{t('auth.code_label')}</label>
                  <input
                    id="fp-code"
                    type="text"
                    inputMode="numeric"
                    placeholder="123456"
                    maxLength={6}
                    autoComplete="one-time-code"
                    value={code}
                    onChange={(e) => { setCode(e.target.value.replace(/\D/g, '')); setError(null); }}
                    disabled={isLoading}
                    required
                  />
                </div>

                <PasswordInput
                  id="fp-new-pw"
                  label={t('auth.new_password')}
                  autoComplete="new-password"
                  value={newPassword}
                  onChange={(e) => { setNewPassword(e.target.value); setError(null); }}
                  disabled={isLoading}
                  required
                />

                <PasswordInput
                  id="fp-confirm-pw"
                  label={t('auth.confirm_password')}
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => { setConfirmPassword(e.target.value); setError(null); }}
                  disabled={isLoading}
                  required
                />

                {error && (
                  <p style={{
                    color: 'var(--danger)', fontSize: '0.85rem', margin: 0,
                    padding: '10px 14px', background: '#FEF2F2', borderRadius: '8px', lineHeight: 1.5,
                  }}>
                    ⚠️ {error}
                  </p>
                )}

                <button type="submit" className="btn btn-primary" disabled={isLoading}>
                  {isLoading ? '…' : t('auth.confirm_reset')}
                </button>

                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={handleRequestCode}
                  disabled={isLoading}
                  style={{ fontSize: '0.875rem' }}
                >
                  {t('auth.resend_code')}
                </button>
              </form>
            </>
          )}

          <hr className="divider" style={{ marginTop: '24px' }} />

          <div style={{ textAlign: 'center' }}>
            <Link to="/login" className="link-secondary">
              {t('auth.back_to_login')}
            </Link>
          </div>

        </div>
      </div>
    </div>
  );
}
